class Wishlist {
  constructor() {
    this.wishlistItems = this.getItems();
    this.isUpdating = false;
    this.init();
  }

  init() {
    document.addEventListener("click", (e) => {
      const button = e.target.closest(".wishlist-button");
      if (button) {
        e.preventDefault();
        const productId = button.dataset.productId;
        const variantId = button.dataset.variantId;
        this.toggleItem(productId, variantId);
      }
    });
    document.addEventListener("cart:updated", (event) => {
      const wishlistAttribute = event.detail?.cart?.attributes?.wishlist || "";
      const newItems = wishlistAttribute.split(",").filter(Boolean);
      if (!this.arraysEqual(this.wishlistItems, newItems)) {
        this.wishlistItems = newItems;
        this.updateUI();
      }
    });
  }

  getItems() {
    const wishlist = window.theme?.cartAttributes?.wishlist || "";
    return wishlist.split(",").filter(Boolean);
  }

  async addItem(productId, variantId) {
    if (this.isUpdating) return;
    const itemId = variantId ? `${productId}:${variantId}` : productId;
    if (this.wishlistItems.includes(itemId)) return;
    const previousItems = [...this.wishlistItems];
    const newItems = [...this.wishlistItems, itemId];
    this.isUpdating = true;
    this.wishlistItems = newItems;
    try {
      this.updateUI();
      await this.updateCartAttributes(newItems);
    } catch (error) {
      this.wishlistItems = previousItems;
      this.updateUI();
      throw error;
    } finally {
      this.isUpdating = false;
    }
  }

  async removeItem(productId, variantId) {
    if (this.isUpdating) return;
    const itemId = variantId ? `${productId}:${variantId}` : productId;
    if (!this.wishlistItems.includes(itemId)) return;
    const previousItems = [...this.wishlistItems];
    const newItems = this.wishlistItems.filter((item) => item !== itemId);
    this.isUpdating = true;
    this.wishlistItems = newItems;
    try {
      this.updateUI();
      await this.updateCartAttributes(newItems);
    } catch (error) {
      this.wishlistItems = previousItems;
      this.updateUI();
      console.error(error);
      throw error;
    } finally {
      this.isUpdating = false;
    }
  }

  clear() {
    if (this.isUpdating) return;
    this.isUpdating = true;
    this.wishlistItems = [];
    this.updateUI(); // Optimistic UI update
    this.updateCartAttributes([])
      .catch(() => {
        this.wishlistItems = this.getItems();
        this.updateUI(); // Rollback UI
      })
      .finally(() => {
        this.isUpdating = false;
      });
  }

  async toggleItem(productId, variantId) {
    if (this.isUpdating) return;
    const itemId = variantId ? `${productId}:${variantId}` : productId;
    try {
      if (this.wishlistItems.includes(itemId)) {
        await this.removeItem(productId, variantId);
      } else {
        await this.addItem(productId, variantId);
      }
    } catch {
      // Handle error if needed
    }
  }

  async updateCartAttributes(newItems) {
    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          attributes: { wishlist: newItems.join(",") },
        }),
      });

      if (!response.ok) throw new Error("Update failed");
      const cart = await response.json();

      window.theme.cartAttributes = {
        ...window.theme.cartAttributes,
        wishlist: cart.attributes?.wishlist || "",
      };

      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart },
        })
      );
    } catch (error) {
      console.error("Wishlist update failed:", error);
      throw error;
    }
  }

  updateUI() {
    this.updateButtons();
    this.updateCounter();
  }

  updateButtons() {
    document.querySelectorAll(".wishlist-button").forEach((button) => {
      const productId = button.dataset.productId;
      const variantId = button.dataset.variantId;
      const itemId = variantId ? `${productId}:${variantId}` : productId;
      const isActive = this.wishlistItems.includes(itemId);
      button.setAttribute(
        "aria-label",
        isActive ? "Remove from wishlist" : "Add to wishlist"
      );
      button.setAttribute("aria-pressed", isActive);
      button.classList.toggle("active", isActive);
    });
  }

  updateCounter() {
    const count = this.wishlistItems.length;
    document
      .querySelectorAll("[data-wishlist-count-indicator]")
      .forEach((element) => {
        element.textContent = count;
        element.classList.toggle("hide", count === 0);
      });
  }

  arraysEqual(a, b) {
    return a.length === b.length && a.every((val, index) => val === b[index]);
  }
}

// Initialization remains the same
document.addEventListener("DOMContentLoaded", () => {
  window.theme = window.theme || {};
  window.theme.wishlist = new Wishlist();
});
